import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'

interface Notification {
  id: string
  type: string
  title: string
  message: string
  link?: string
  priority: string
  read: boolean
  archived: boolean
  created_at: string
  read_at?: string
}

interface NotificationType {
  id: string
  name: string
  icon: string
  color: string
}

interface NotificationCategory {
  id: string
  name: string
}

const API_BASE = '/api/notification-center'

const typeIcons: Record<string, string> = {
  system: '⚙️',
  alert: '⚠️',
  success: '✅',
  info: 'ℹ️',
  report: '📊',
  market: '📈',
  compliance: '✓',
  ai: '🤖',
}

const typeColors: Record<string, string> = {
  system: '#6B7280',
  alert: '#EF4444',
  success: '#10B981',
  info: '#3B82F6',
  report: '#8B5CF6',
  market: '#F59E0B',
  compliance: '#EC4899',
  ai: '#7C3AED',
}

// Priority colors for future use
const _priorityColors: Record<string, string> = {
  high: '#EF4444',
  normal: '#6B7280',
  low: '#9CA3AF',
}
void _priorityColors // Suppress unused warning

export default function NotificationCenter() {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([])
  const [categories, setCategories] = useState<NotificationCategory[]>([])
  const [activeCategory, setActiveCategory] = useState('all')
  const [unreadCount, setUnreadCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<Record<string, unknown>>({})

  useEffect(() => {
    fetchConfiguration()
    fetchNotifications()
    fetchPreferences()
  }, [])

  useEffect(() => {
    fetchNotifications()
  }, [activeCategory])

  const fetchConfiguration = async () => {
    try {
      const [typesRes, categoriesRes] = await Promise.all([
        fetch(`${API_BASE}/types`),
        fetch(`${API_BASE}/categories`)
      ])
      setNotificationTypes(await typesRes.json())
      setCategories(await categoriesRes.json())
    } catch (error) {
      console.error('Failed to fetch configuration:', error)
    }
  }

  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/notifications?category=${activeCategory}`)
      const data = await res.json()
      setNotifications(data.notifications || [])
      setUnreadCount(data.unread_count || 0)
      setTotal(data.total || 0)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API_BASE}/preferences`)
      setPreferences(await res.json())
    } catch (error) {
      console.error('Failed to fetch preferences:', error)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}/read`, { method: 'POST' })
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ))
      setUnreadCount(Math.max(0, unreadCount - 1))
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_BASE}/notifications/read-all`, { method: 'POST' })
      setNotifications(notifications.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  const archiveNotification = async (notificationId: string) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}/archive`, { method: 'POST' })
      setNotifications(notifications.filter(n => n.id !== notificationId))
      setTotal(total - 1)
    } catch (error) {
      console.error('Failed to archive:', error)
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      await fetch(`${API_BASE}/notifications/${notificationId}`, { method: 'DELETE' })
      const notif = notifications.find(n => n.id === notificationId)
      setNotifications(notifications.filter(n => n.id !== notificationId))
      setTotal(total - 1)
      if (notif && !notif.read) {
        setUnreadCount(Math.max(0, unreadCount - 1))
      }
    } catch (error) {
      console.error('Failed to delete:', error)
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    if (notification.link) {
      navigate(notification.link)
    }
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />

      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
              Notification Center
            </h1>
            <p style={{ color: '#6B7280', marginTop: '4px' }}>
              {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Mark All Read
              </button>
            )}
            <button
              onClick={() => setShowPreferences(!showPreferences)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              ⚙️ Settings
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '24px' }}>
          {/* Sidebar - Categories */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: 'fit-content'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
              Filter
            </h3>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: activeCategory === category.id ? '#EFF6FF' : 'transparent',
                  color: activeCategory === category.id ? '#4A90E2' : '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontWeight: activeCategory === category.id ? '600' : '400',
                  marginBottom: '4px',
                  transition: 'all 0.2s'
                }}
              >
                {category.name}
                {category.id === 'unread' && unreadCount > 0 && (
                  <span style={{
                    marginLeft: '8px',
                    backgroundColor: '#EF4444',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}

            <div style={{ borderTop: '1px solid #E5E7EB', marginTop: '16px', paddingTop: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                Types
              </h3>
              {notificationTypes.map(type => (
                <div
                  key={type.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 0',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                >
                  <span>{typeIcons[type.id]}</span>
                  <span>{type.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content - Notifications */}
          <div>
            {/* Preferences Panel */}
            {showPreferences && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Notification Preferences</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(preferences.email_notifications)}
                      onChange={() => {}}
                    />
                    <span>Email Notifications</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(preferences.push_notifications)}
                      onChange={() => {}}
                    />
                    <span>Push Notifications</span>
                  </label>
                </div>
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email Digest</label>
                  <select
                    value={String(preferences.email_digest || 'daily')}
                    onChange={() => {}}
                    style={{
                      padding: '10px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      width: '200px'
                    }}
                  >
                    <option value="none">None</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>
            )}

            {/* Notifications List */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {loading ? (
                <div style={{ padding: '48px', textAlign: 'center', color: '#6B7280' }}>
                  Loading notifications...
                </div>
              ) : notifications.length === 0 ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
                  <h3 style={{ margin: '0 0 8px 0' }}>No Notifications</h3>
                  <p style={{ color: '#6B7280', margin: 0 }}>
                    {activeCategory === 'unread' ? 'You\'re all caught up!' : 'No notifications in this category'}
                  </p>
                </div>
              ) : (
                notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    style={{
                      padding: '16px 20px',
                      borderBottom: index < notifications.length - 1 ? '1px solid #F3F4F6' : 'none',
                      backgroundColor: notification.read ? 'white' : '#F0F9FF',
                      cursor: notification.link ? 'pointer' : 'default',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => notification.link && handleNotificationClick(notification)}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = notification.read ? '#F9FAFB' : '#E0F2FE'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = notification.read ? 'white' : '#F0F9FF'}
                  >
                    <div style={{ display: 'flex', gap: '16px' }}>
                      {/* Type Icon */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        backgroundColor: `${typeColors[notification.type]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        flexShrink: 0
                      }}>
                        {typeIcons[notification.type]}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <h4 style={{
                            margin: 0,
                            fontSize: '15px',
                            fontWeight: notification.read ? '500' : '600',
                            color: '#111827'
                          }}>
                            {notification.title}
                          </h4>
                          {notification.priority === 'high' && (
                            <span style={{
                              padding: '2px 8px',
                              backgroundColor: '#FEE2E2',
                              color: '#DC2626',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              HIGH
                            </span>
                          )}
                          {!notification.read && (
                            <span style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#4A90E2'
                            }} />
                          )}
                        </div>
                        <p style={{
                          margin: '0 0 8px 0',
                          fontSize: '14px',
                          color: '#6B7280',
                          lineHeight: '1.5'
                        }}>
                          {notification.message}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#9CA3AF' }}>
                          <span>{formatTime(notification.created_at)}</span>
                          <span style={{
                            padding: '2px 8px',
                            backgroundColor: `${typeColors[notification.type]}15`,
                            color: typeColors[notification.type],
                            borderRadius: '4px'
                          }}>
                            {notificationTypes.find(t => t.id === notification.type)?.name || notification.type}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                        {!notification.read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsRead(notification.id) }}
                            title="Mark as read"
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#6B7280',
                              borderRadius: '6px'
                            }}
                          >
                            ✓
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); archiveNotification(notification.id) }}
                          title="Archive"
                          style={{
                            padding: '8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#6B7280',
                            borderRadius: '6px'
                          }}
                        >
                          📥
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id) }}
                          title="Delete"
                          style={{
                            padding: '8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#EF4444',
                            borderRadius: '6px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Stats Footer */}
            {total > 0 && (
              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#6B7280',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span>Showing {notifications.length} of {total} notifications</span>
                <span>{unreadCount} unread</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
